import { inject, Injectable, OnDestroy } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { Router } from "@angular/router";
import { Subscription, Observable, BehaviorSubject } from "rxjs";
import { GlobalFeatureModel } from "@core/models/global-feature.model";
import { GlobalStorageService } from "../global-storage/global-storage.service";
import { SessionCleanupService } from "../session-cleanup/session-cleanup.service";
import { CheckForUpdateService } from "../check-for-update/check-for-update.service";
import { firstValueFrom } from "rxjs";
import { environment } from "@environments/environment";

interface FirestoreTimestampLike {
  toDate(): Date;
}

interface GlobalFeatureFlags {
  excuelawebMaintenanceMode: boolean;
  checkForUpdate: boolean;
  [key: string]: boolean;
}

interface GlobalFeatureFirestoreData {
  featureFlags: GlobalFeatureFlags;
  dateChangeVersion: FirestoreTimestampLike;
}

function isGlobalFeatureFirestoreData(value: unknown): value is GlobalFeatureFirestoreData {
  if (value == null || typeof value !== "object") return false;

  const data: Partial<GlobalFeatureFirestoreData> = value as Partial<GlobalFeatureFirestoreData>;
  return (
    data.featureFlags != null &&
    typeof data.featureFlags === "object" &&
    data.dateChangeVersion != null &&
    typeof data.dateChangeVersion === "object" &&
    typeof (data.dateChangeVersion as FirestoreTimestampLike).toDate === "function"
  );
}

const MAINTENANCE_MODE_CACHE_KEY: string = "excuelawebMaintenanceMode";

@Injectable({
  providedIn: "root",
})
export class GlobalChangesService implements OnDestroy {
  private subscription?: Subscription;
  private globalFeatureDocId: string = "excuela-global-feature";
  public globalFeatureData!: GlobalFeatureModel;
  private previousMaintenanceMode: boolean | null = null;
  private lastGuardAnswer: boolean | null = null;
  private dataLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public dataLoaded$: Observable<boolean> = this.dataLoadedSubject.asObservable();

  private readonly firestore: AngularFirestore = inject(AngularFirestore);
  private readonly globalStorage: GlobalStorageService = inject(GlobalStorageService);
  private readonly sessionCleanupService: SessionCleanupService = inject(SessionCleanupService);
  private readonly checkForUpdateService: CheckForUpdateService = inject(CheckForUpdateService);
  private readonly router: Router = inject(Router);

  public listenToGlobalChanges(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if(!environment.maintenanceMode) {
      return;
    }

    this.subscription = this.firestore.collection("subdomains").doc(this.globalFeatureDocId).valueChanges().subscribe((data: unknown) => {
        this.handleGlobalChanges(data);
      });
  }


  /** Último valor conocido del flag, sin red. */
  public isMaintenanceModeCached(): boolean {
    if (!environment.maintenanceMode) return false;

    const answer: boolean = this.globalFeatureData
      ? Boolean(this.globalFeatureData.featureFlags?.["excuelawebMaintenanceMode"])
      : this.globalStorage.getStorage<boolean>(MAINTENANCE_MODE_CACHE_KEY) === true;

    this.lastGuardAnswer = answer;

    return answer;
  }

  private reconcileGuardDecision(isMaintenanceMode: boolean): void {
    if (this.lastGuardAnswer === null) return;
    if (this.lastGuardAnswer === isMaintenanceMode) return;

    if (isMaintenanceMode) this.sessionCleanupService.cleanupSessions();

    this.router.navigate([isMaintenanceMode ? "/maintenance-mode" : "/catalogue"]);
  }

  public async waitForData(): Promise<GlobalFeatureModel | null> {
    if(!environment.maintenanceMode) return Promise.resolve(null);

    const timeoutPromise: Promise<null> = new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), 3000);
    });
  
    const dataPromise: Promise<GlobalFeatureModel | null> = firstValueFrom(
      this.firestore.collection("subdomains").doc(this.globalFeatureDocId).get()
    ).then((data) => {
      if (!data.exists) return null;
      const value: unknown = data.data();
      if (!isGlobalFeatureFirestoreData(value)) return null;
      return {
        ...value,
        dateChangeVersion: value.dateChangeVersion.toDate(),
      };
    });
  
    return Promise.race([dataPromise, timeoutPromise]);
  }



  private async handleGlobalChanges(data: unknown): Promise<void> {
    if (!isGlobalFeatureFirestoreData(data)) return;

    this.globalFeatureData = {
      ...data, 
      dateChangeVersion: data.dateChangeVersion.toDate()
    };

    const { excuelawebMaintenanceMode: newMaintenanceMode, checkForUpdate } = this.globalFeatureData.featureFlags;
    const newDateChangeVersion: Date = this.globalFeatureData.dateChangeVersion;

    this.globalStorage.createToken(MAINTENANCE_MODE_CACHE_KEY, newMaintenanceMode);

    if (!this.dataLoadedSubject.value) {
      this.dataLoadedSubject.next(true);
    }

    const isFirstSnapshot: boolean = this.previousMaintenanceMode === null;

    if (!isFirstSnapshot && this.previousMaintenanceMode !== newMaintenanceMode) {

      if(newMaintenanceMode) this.sessionCleanupService.cleanupSessions();

      if(!newMaintenanceMode && checkForUpdate && (new Date() > newDateChangeVersion)) {
        console.log("Checking for update...");
        await this.checkForUpdateService.checkUpdate();
      }

      const allowedRoute: string = newMaintenanceMode ? "/maintenance-mode" : "/catalogue";
      this.router.navigate([allowedRoute]);
    } else if (isFirstSnapshot) {
      this.reconcileGuardDecision(newMaintenanceMode);
    }

    this.previousMaintenanceMode = newMaintenanceMode;
  }


  public stopListening(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }


  ngOnDestroy(): void {
    this.stopListening();
  }
}
